import './DetailHeaderMedia.less';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import FloatingDock, { FloatingDockClose } from '@/common/components/FloatingDock';
import { BetradarMTLoadable, BetradarMTScoreboardLoadable } from '@/matchtracker/RadarLoadable';
import VideoStream from '@/player/VideoStreamNg';
import { match$$ } from 'nike-ui/src/components/turboLive/push/overview/subs';
import { useReframe } from 'nike-re-framejs';
import { MatchDetailIcons } from 'nike-ui/src/components/turboLive/components/detail/MatchDetailScoreboard';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import { isRadarStream, isVideoStream } from 'nike-ui/src/components/turboLive/push/overview/handlers';
import { useLoadMatchtrackerTemplates } from 'nike-ui/src/components/match-statistics/hooks/useLoadMatchtrackerTemplates';
import { useIsBetradarWidgetAvailable } from 'nike-ui/src/components/match-statistics/hooks/useIsBetradarWidgetAvailable';

const RADAR = 'RADAR';
const VIDEO = 'VIDEO';

const DetailHeaderMedia = memo(function DetailHeaderMedia({
    sportId,
    eventId,
    onUpdate,
    sportCode,
    statisticsId,
    sportEvent,
    tournamentId
}) {
    const { derefSub } = useReframe();
    useLoadMatchtrackerTemplates();
    const match = sportEvent || derefSub(match$$(sportCode, eventId));
    const isWidgetAvailable = useIsBetradarWidgetAvailable(match);
    const hasRadar = isRadarStream(match);
    const hasVideo = isVideoStream(match);
    const [media, setMedia] = useState(null);
    const activeVideo = media === VIDEO;
    const activeRadar = media === RADAR;
    useEffect(() => {
        if (onUpdate) {
            onUpdate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasVideo, hasRadar, media]);

    const getBtnClassName = active =>
        clsx('live-detail-ico bg-white flex-center fs-0 br-radius-circle mx-10', {
            'live-detail-bg-opacity-5': !active
        });
    const toggleTab = newTab => {
        const newMedia = media === newTab ? null : newTab;
        setMedia(newMedia);
    };

    return (
        <>
            <div className={`bg-${sportId}`}>
                <BetradarMTScoreboardLoadable sportId={sportId} event={match} sportCode={sportCode} />
            </div>
            <div className={clsx(`live-detail-header-media bg-${sportId}`)}>
                <>
                    <div className={clsx('flex-center', { 'pr-10': hasVideo || hasRadar })}>
                        {(hasVideo || hasRadar) && (
                            <div className="flex-1 flex items-center fs-14 text-bold py-10 px-10">
                                {hasVideo && (
                                    <button
                                        className={getBtnClassName(activeVideo)}
                                        data-atid="match-detail-header-video"
                                        onClick={() => toggleTab(VIDEO)}
                                    >
                                        <SvgIcon
                                            imgId="live-play-stream"
                                            sizeX="14"
                                            sizeY="16"
                                            className="c-black-50 ml-3"
                                        />
                                    </button>
                                )}
                                {hasRadar && (
                                    <button
                                        className={getBtnClassName(activeRadar)}
                                        data-atid="match-detail-header-radar"
                                        onClick={() => toggleTab(RADAR)}
                                    >
                                        <SvgIcon imgId="live-play-radar" sizeX="22" sizeY="15" className="c-black-50" />
                                    </button>
                                )}
                            </div>
                        )}
                        <MatchDetailIcons matchId={eventId} sportId={sportId} statisticsId={statisticsId} />
                    </div>

                    {(media === VIDEO || hasRadar) && (
                        <FloatingDock>
                            {(shownDock, closeDock) => (
                                <div className={`live-detail-header-media-content ${shownDock ? 'docked' : ''}`}>
                                    {shownDock && <FloatingDockClose closeDock={closeDock} />}
                                    {media === VIDEO && (
                                        <VideoStream
                                            eventId={eventId}
                                            sportId={sportId}
                                            active={activeVideo}
                                            tournamentId={tournamentId}
                                        />
                                    )}
                                    {media === RADAR && isWidgetAvailable ? (
                                        <BetradarMTLoadable event={match} sportId={sportId} />
                                    ) : null}
                                </div>
                            )}
                        </FloatingDock>
                    )}
                </>
            </div>
        </>
    );
});

DetailHeaderMedia.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    statisticsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onUpdate: PropTypes.func,
    sportEvent: PropTypes.any
};

export default DetailHeaderMedia;
